import { Form, FormInstance } from "antd";
import { AppPartType } from "@lhd/features/Apps/AppForm/types";
import { DATE_TIME_FORMAT } from "@lhd/constants";
import { Styles } from "./styled";
import dayjs from "dayjs";
import { getRuleValidation } from "@lhd/components/CloneApp/fieldUtils";
import { KeyboardEvent, useRef, useState } from "react";
import {
  parseDatePickerInput,
  parseFullWidthToHalfWidth,
  toDatePickerApiDate,
} from "@lhd/utility";
import { APP_TYPES } from "../../constants";
import CommonDatePicker from "@lhd/components/CommonDatePicker";
import moment, { Moment } from "moment";

interface IProps {
  item: AppPartType;
  styleColor?: React.CSSProperties;
  namePath: string | string[];
  allowSearchDate?: boolean;
  form?: FormInstance;
  onChange?: (value: dayjs.Dayjs | null, dateString: string) => void;
  isDisabled?: boolean;
  appPartsFlat?: AppPartType[];
  appType?: string;
}

const toApiDateTime = (raw: string | Moment | null | undefined) => {
  if (raw == null || raw === "") return null;
  if (moment.isMoment(raw)) {
    return raw.isValid()
      ? raw.clone().startOf("day").format(DATE_TIME_FORMAT.DATE_TIME_DASH_SEC)
      : null;
  }
  const api = toDatePickerApiDate(raw);
  if (!api) return null;
  return moment(api, DATE_TIME_FORMAT.DATE_DASH, true)
    .startOf("day")
    .format(DATE_TIME_FORMAT.DATE_TIME_DASH_SEC);
};

type DateInputProps = {
  value?: string | null;
  onChange?: (value: string | null) => void;
  disabled?: boolean;
  isMonth?: boolean;
  styleColor?: React.CSSProperties;
  pickerKey: number;
  setPickerKey: React.Dispatch<React.SetStateAction<number>>;
  allowSearchDate?: boolean;
  isComposingRef: React.MutableRefObject<boolean>;
  typingTimeout: React.MutableRefObject<any>;
  applySearch: (input: string) => void;
  onChangeProp?: (value: dayjs.Dayjs | null, dateString: string) => void;
};

const CloneAppDateInput = ({
  value,
  onChange,
  disabled,
  isMonth,
  styleColor,
  pickerKey,
  setPickerKey,
  allowSearchDate,
  isComposingRef,
  typingTimeout,
  applySearch,
  onChangeProp,
}: DateInputProps) => {
  const commit = (raw: string | Moment | null) => {
    if (raw == null || (typeof raw === "string" && !raw.trim())) {
      onChange?.(null);
      onChangeProp?.(null, "");
      return;
    }

    let momentValue: Moment | null = null;
    if (moment.isMoment(raw)) {
      momentValue = raw;
    } else if (isMonth) {
      momentValue = moment(
        String(raw).replace(/度/g, "").trim(),
        [DATE_TIME_FORMAT.MONTH_YEAR_JP, "YYYY年M月", "YYYY/MM", "YYYY-MM"],
        true,
      );
      if (!momentValue.isValid()) {
        momentValue = parseDatePickerInput(String(raw));
      }
    } else {
      const api = toDatePickerApiDate(raw);
      momentValue = api
        ? moment(api, DATE_TIME_FORMAT.DATE_DASH, true)
        : parseDatePickerInput(raw);
    }

    if (!momentValue || !momentValue.isValid()) return;

    const saved = isMonth
      ? momentValue
          .clone()
          .startOf("month")
          .format(DATE_TIME_FORMAT.DATE_TIME_DASH_SEC)
      : toApiDateTime(momentValue);

    onChange?.(saved);
    onChangeProp?.(
      dayjs(saved),
      isMonth
        ? momentValue.format(DATE_TIME_FORMAT.MONTH_YEAR_JP) + "度"
        : momentValue.format(DATE_TIME_FORMAT.DATE_JP),
    );
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "Enter") return;
    event.preventDefault();
    event.stopPropagation();
    const input = event.currentTarget.querySelector("input");
    commit(input?.value ?? "");
  };

  // Form keeps "YYYY-MM-DD HH:mm:ss"; picker needs a date-only value to display.
  const pickerValue = (() => {
    if (value == null || value === "") return null;
    if (isMonth) {
      const m = moment(value);
      return m.isValid() ? m : null;
    }
    return (
      toDatePickerApiDate(value) ||
      (moment(value).isValid()
        ? moment(value).format(DATE_TIME_FORMAT.DATE_DASH)
        : null)
    );
  })();

  return (
    <CommonDatePicker
      key={pickerKey}
      saveFormat="api"
      picker={isMonth ? "month" : undefined}
      displayFormat={
        isMonth
          ? DATE_TIME_FORMAT.MONTH_YEAR_JP + "度"
          : DATE_TIME_FORMAT.DATE_JP
      }
      disabled={disabled}
      allowClear
      // Same as antd ja_JP DatePicker default when placeholder is omitted.
      placeholder="日付を選択"
      style={styleColor}
      value={pickerValue}
      onKeyDown={(e) => {
        handleKeyDown(e);
        if (!allowSearchDate || isComposingRef.current) return;
        if (e.key === "Enter") return;
        clearTimeout(typingTimeout.current);
        typingTimeout.current = setTimeout(() => {
          const input = (e.currentTarget as HTMLElement).querySelector("input");
          applySearch(input?.value ?? "");
        }, 400);
      }}
      onChange={(_saved, date) => {
        if (isMonth) {
          commit(date);
          return;
        }
        commit(_saved);
      }}
      onBlur={() => {
        if (allowSearchDate) {
          setTimeout(() => setPickerKey((k) => k + 1), 300);
        }
      }}
    />
  );
};

const DateCustom = (props: IProps) => {
  const {
    item,
    namePath,
    styleColor = {},
    allowSearchDate,
    form,
    onChange,
    isDisabled,
    appPartsFlat,
    appType,
  } = props;
  const typingTimeout = useRef<any>(null);
  const isComposingRef = useRef(false);
  const [pickerKey, setPickerKey] = useState(0);
  const isMonth = item.field_code === "settlement_date";

  const applySearch = (input: string) => {
    const raw = parseFullWidthToHalfWidth(input?.trim());

    let parsed: dayjs.Dayjs | null = null;

    // full date: YYYY年MM月DD日
    const fullDateMatch = raw.match(/^(\d{4})年(\d{1,2})月(\d{1,2})日?$/);
    if (fullDateMatch) {
      parsed = dayjs(
        `${fullDateMatch[1]}-${fullDateMatch[2]}-${fullDateMatch[3]}`,
        "YYYY-M-D",
      );
    }

    // year
    else if (/^\d{4}$/.test(raw)) {
      parsed = dayjs().year(Number(raw)).startOf("year");
    }

    // 1–2 digits
    else if (/^\d{1,2}$/.test(raw)) {
      const num = Number(raw);

      // month
      if (num >= 1 && num <= 12) {
        parsed = dayjs()
          .month(num - 1)
          .startOf("month");
      }

      // day (when > 12)
      else if (num >= 13 && num <= 31) {
        parsed = dayjs().date(num);
      }
    }

    if (parsed?.isValid()) {
      form?.setFields([
        {
          name: namePath,
          value: parsed.format(DATE_TIME_FORMAT.DATE_TIME_DASH_SEC),
        },
      ]);

      setPickerKey((k) => k + 1);
    }
  };

  return (
    <Styles background={styleColor?.background} color={styleColor?.color}>
      <div
        onCompositionStart={() => {
          isComposingRef.current = true;
        }}
        onCompositionEnd={(e: any) => {
          isComposingRef.current = false;
          if (!allowSearchDate) return;
          clearTimeout(typingTimeout.current);
          typingTimeout.current = setTimeout(() => {
            applySearch(e.target.value);
          }, 100);
        }}
      >
        <Form.Item
          name={namePath}
          rules={(() => {
            const rules = [...getRuleValidation(item)];
            if (
              item.name === "終了日" &&
              appType === APP_TYPES.BUSINESS_TRIP_REPORTS &&
              appPartsFlat
            ) {
              const startPart = appPartsFlat.find((p) => p.name === "出発日");
              if (startPart && form) {
                rules.push({
                  validator: (_, value) => {
                    const startPath = Array.isArray(namePath)
                      ? [...namePath.slice(0, -1), String(startPart.id)]
                      : String(startPart.id);
                    const startDateValue = form.getFieldValue(startPath);
                    if (value && startDateValue) {
                      const start = dayjs(startDateValue);
                      const end = dayjs(value);
                      if (
                        start.isValid() &&
                        end.isValid() &&
                        end.isBefore(start, "day")
                      ) {
                        return Promise.reject(
                          new Error(
                            "終了日は出発日以降の日付を指定してください。",
                          ),
                        );
                      }
                    }
                    return Promise.resolve();
                  },
                });
              }
            }
            return rules;
          })()}
          dependencies={
            item.name === "終了日" &&
            appType === APP_TYPES.BUSINESS_TRIP_REPORTS &&
            appPartsFlat
              ? [
                  Array.isArray(namePath)
                    ? [
                        ...namePath.slice(0, -1),
                        String(
                          appPartsFlat.find((p) => p.name === "出発日")?.id ||
                            "",
                        ),
                      ]
                    : [
                        String(
                          appPartsFlat.find((p) => p.name === "出発日")?.id ||
                            "",
                        ),
                      ],
                ]
              : undefined
          }
          getValueProps={(value) => ({
            value: value || null,
          })}
        >
          <CloneAppDateInput
            disabled={isDisabled}
            isMonth={isMonth}
            styleColor={styleColor}
            pickerKey={pickerKey}
            setPickerKey={setPickerKey}
            allowSearchDate={allowSearchDate}
            isComposingRef={isComposingRef}
            typingTimeout={typingTimeout}
            applySearch={applySearch}
            onChangeProp={onChange}
          />
        </Form.Item>
      </div>
    </Styles>
  );
};
export default DateCustom;
